<h2 mat-dialog-title>{{ 'TEMPLATE_CREATE.TITLE' | translate }}</h2>

<mat-dialog-content class="!pt-4">
  <form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col gap-2" style="max-width: 700px;">

    <div *ngIf="saveError" class="text-status-error text-sm p-2 bg-red-50 rounded whitespace-pre-line">
      {{ saveError }}
    </div>

    <!-- Stammdaten -->
    <h3 class="text-sm font-semibold text-muted uppercase tracking-wide">
      {{ 'TEMPLATE_CREATE.SECTION_MASTER_DATA' | translate }}
    </h3>

    <!-- Org-Selector. subscriptSizing="dynamic": der lange Hinweis schiebt
         das nächste Feld nach unten, statt es zu überlappen (DOCU-1358). -->
    <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
      <mat-label>{{ 'TEMPLATE_CREATE.ORG_ASSIGNMENT' | translate }}</mat-label>
      <input matInput
             [formControl]="orgSearchControl"
             [matAutocomplete]="orgAuto"
             [placeholder]="'TEMPLATE_CREATE.ORG_SEARCH_PLACEHOLDER' | translate">
      <button *ngIf="selectedOrg" matSuffix mat-icon-button (click)="clearOrg()" type="button">
        <mat-icon>close</mat-icon>
      </button>
      <mat-autocomplete #orgAuto="matAutocomplete"
                        [displayWith]="displayOrg"
                        (optionSelected)="onOrgSelected($event.option.value)">
        <mat-option *ngIf="orgsLoading" disabled>
          <mat-spinner diameter="16" class="inline-block mr-2"></mat-spinner>
          {{ 'TEMPLATE_CREATE.ORG_LOADING' | translate }}
        </mat-option>
        <mat-option *ngFor="let org of filteredOrgs" [value]="org">
          <span class="font-medium">{{ org.displayName }}</span>
          <span class="text-xs text-muted ml-2">{{ org.subscriptionKey }}</span>
        </mat-option>
      </mat-autocomplete>
      <mat-hint>{{ isForOrg ? ('TEMPLATE_CREATE.ORG_SELECTED_HINT' | translate) : ('TEMPLATE_CREATE.ORG_ASSIGNMENT_HINT' | translate) }}</mat-hint>
    </mat-form-field>

    <!-- Info-Banner wenn Org gewählt -->
    <div *ngIf="selectedOrg" class="flex items-center gap-2 p-3 rounded bg-blue-50 border border-blue-200 text-sm text-blue-800">
      <mat-icon class="!text-base text-blue-600">info</mat-icon>
      <span>{{ 'TEMPLATE_CREATE.ORG_MODE_INFO' | translate : { name: selectedOrg.displayName } }}</span>
    </div>

    <!-- Name -->
    <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
      <mat-label>{{ 'TEMPLATE_CREATE.NAME' | translate }}</mat-label>
      <input matInput formControlName="name" required
             [placeholder]="'TEMPLATE_CREATE.NAME_PLACEHOLDER' | translate">
      <mat-error *ngIf="form.controls.name.hasError('required')">
        {{ 'TEMPLATE_CREATE.NAME_REQUIRED' | translate }}
      </mat-error>
    </mat-form-field>

    <!-- Description -->
    <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
      <mat-label>{{ 'TEMPLATE_CREATE.DESCRIPTION' | translate }}</mat-label>
      <input matInput formControlName="description"
             [placeholder]="'TEMPLATE_CREATE.DESCRIPTION_PLACEHOLDER' | translate">
    </mat-form-field>

    <!-- Katalog + Mindest-Tarif – nur für System-Vorlagen -->
    <div *ngIf="!isForOrg" class="flex gap-4">
      <mat-form-field appearance="outline" class="flex-1" subscriptSizing="dynamic">
        <mat-label>{{ 'TEMPLATE_CREATE.CATALOG_KEY' | translate }}</mat-label>
        <mat-select formControlName="catalogKey">
          <mat-option value="">–</mat-option>
          <mat-option *ngFor="let p of planOptions" [value]="p.value">{{ p.label }}</mat-option>
        </mat-select>
        <mat-hint>{{ 'TEMPLATE_CREATE.CATALOG_KEY_HINT' | translate }}</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" class="flex-1" subscriptSizing="dynamic">
        <mat-label>{{ 'TEMPLATE_CREATE.MIN_PLAN' | translate }}</mat-label>
        <mat-select formControlName="minimalPlanKey">
          <mat-option value="">–</mat-option>
          <mat-option *ngFor="let p of planOptions" [value]="p.value">{{ p.label }}</mat-option>
        </mat-select>
        <mat-hint>{{ 'TEMPLATE_CREATE.MIN_PLAN_HINT' | translate }}</mat-hint>
      </mat-form-field>
    </div>

    <!-- Formular -->
    <h3 class="text-sm font-semibold text-muted uppercase tracking-wide mt-4">
      {{ 'TEMPLATE_CREATE.SECTION_FORM' | translate }}
    </h3>

    <mat-radio-group formControlName="source" class="flex flex-col gap-1">
      <mat-radio-button value="empty">
        <span class="font-medium">{{ 'TEMPLATE_CREATE.SOURCE_EMPTY' | translate }}</span>
        <span class="block text-xs text-muted">{{ 'TEMPLATE_CREATE.SOURCE_EMPTY_HINT' | translate }}</span>
      </mat-radio-button>
      <mat-radio-button value="json">
        <span class="font-medium">{{ 'TEMPLATE_CREATE.SOURCE_JSON' | translate }}</span>
        <span class="block text-xs text-muted">{{ 'TEMPLATE_CREATE.SOURCE_JSON_HINT' | translate }}</span>
      </mat-radio-button>
    </mat-radio-group>

    <ng-container *ngIf="isJsonSource">
      <div class="flex items-center gap-3">
        <button mat-stroked-button type="button" (click)="fileInput.click()">
          <mat-icon>upload_file</mat-icon>
          {{ 'TEMPLATE_CREATE.LOAD_FILE' | translate }}
        </button>
        <span *ngIf="loadedFileName" class="text-xs text-muted">{{ loadedFileName }}</span>
        <input #fileInput type="file" accept=".json,application/json" hidden (change)="onFileSelected($event)">
      </div>

      <mat-form-field appearance="outline" class="w-full" subscriptSizing="dynamic">
        <mat-label>{{ 'TEMPLATE_CREATE.FORM_JSON' | translate }}</mat-label>
        <textarea matInput
                  formControlName="json"
                  rows="10"
                  class="font-mono text-xs"
                  [placeholder]="'TEMPLATE_CREATE.FORM_JSON_PLACEHOLDER' | translate">
        </textarea>
        <mat-hint>{{ 'TEMPLATE_CREATE.FORM_JSON_HINT' | translate }}</mat-hint>
        <mat-error *ngIf="jsonControl.getError('json') as code">
          {{ 'TEMPLATE_CREATE.JSON_ERROR.' + code | translate }}
        </mat-error>
        <mat-error *ngIf="jsonControl.getError('server') as message">
          <span class="whitespace-pre-line">{{ message }}</span>
        </mat-error>
      </mat-form-field>
    </ng-container>

  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="dialogRef.close()" [disabled]="saving" type="button">
    {{ 'COMMON.CANCEL' | translate }}
  </button>
  <button mat-raised-button color="primary" (click)="save()" [disabled]="saving" type="button">
    <mat-spinner *ngIf="saving" diameter="18" class="mr-2 inline-block"></mat-spinner>
    <mat-icon *ngIf="!saving">add</mat-icon>
    {{ (isJsonSource ? 'TEMPLATE_CREATE.SAVE' : 'TEMPLATE_CREATE.SAVE_AND_DESIGN') | translate }}
  </button>
</mat-dialog-actions>
